import { Fragment, useEffect } from 'react'

import { Dialog, Transition } from '@headlessui/react'
import classNames from 'classnames'

import { Box, Card, Icon, Typography } from 'components/atomic'

import { ModalProps } from './types'

export const Modal = ({
  title,
  isOpened,
  withCard = true,
  size = 'default',
  children,
  onClose,
}: ModalProps) => {
  // Add body class to hide search bar when modal is open
  useEffect(() => {
    if (isOpened) {
      document.body.classList.add('modal-open')
    } else {
      document.body.classList.remove('modal-open')
    }
    return () => {
      document.body.classList.remove('modal-open')
    }
  }, [isOpened])

  return (
    <Transition appear show={isOpened} as={Fragment}>
      <Dialog as={Fragment} onClose={onClose}>
        <div className="fixed inset-0 z-20">
          <div className="relative flex items-start sm:items-center justify-center min-h-screen p-2 pt-8 sm:pt-2 text-center lg:p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 backdrop-blur-xl backdrop-brightness-90 dark:backdrop-brightness-50" />
            </Transition.Child>

            {/*
              https://headlessui.dev/react/dialog#dialog-overlay
              This element is to trick the browser into centering the modal contents.
            */}
            <span
              className="inline-block h-screen align-middle"
              aria-hidden="true"
              // eslint-disable-next-line react/jsx-no-literals
            >
              &#8203;
            </span>

            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div
                className={classNames(
                  'max-w-[95vw] md:max-w-3xl w-full inline-block overflow-y-auto mx-2 drop-shadow-2xl text-left transition-all transform',
                  size === 'wide' && 'lg:max-w-6xl',
                )}
              >
                <div className="flex flex-row items-center justify-between my-2 lg:my-3 px-2 lg:px-0">
                  <Box alignCenter row>
                    <Typography className="text-lg md:text-2xl lg:text-3xl" variant="h3">{title}</Typography>
                  </Box>

                  <Box
                    onClick={onClose}
                    center
                    className={classNames(
                      'h-10 w-10 md:h-12 md:w-12 rounded-2xl self-center border border-solid cursor-pointer hover:brightness-90 dark:hover:brightness-110',
                      'border-light-typo-primary dark:border-dark-typo-primary',
                    )}
                  >
                    <Icon
                      className="self-center"
                      name="close"
                      color="primary"
                      size={24}
                    />
                  </Box>
                </div>
                <Box className="w-full max-h-[85vh] md:max-h-[88vh]">
                  {withCard ? (
                    <Card
                      className="flex items-center justify-center"
                      bodyClassName="overflow-y-auto my-0"
                      stretch
                      size="lg"
                    >
                      {children}
                    </Card>
                  ) : (
                    children
                  )}
                </Box>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
